import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { landingPath, useSession } from '../lib/session';
import { safeReturnPath } from '../lib/sessionClock';
import { ApiError } from '../lib/api';
import type { TwoFactorChallenge } from '../lib/types';
import { Brand } from '../components/Brand';
import { ThemeToggle } from '../components/ThemeToggle';
import { PasswordField } from '../components/PasswordField';

export function LoginPage() {
  const { session, loading, endedReason, signIn, completeTwoFactor } = useSession();
  const from = (useLocation().state as { from?: string } | null)?.from ?? null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Set when the password was right and a second step is owed. */
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState('');

  if (loading) return <div className="empty">Loading…</div>;
  /*
    BACK WHERE THEY WERE, not their landing page. Being signed out mid-task — twelve hours up,
    or signed out in another tab — and then dropped on the home page after signing back in means
    finding your way back to the board you had open. `RequireSession` passes the address along.
    Only an in-app path is honoured, so a crafted link cannot send somebody off-site after they
    type their password.
  */
  if (session) {
    return <Navigate to={safeReturnPath(from) ?? landingPath(session)} replace />;
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      /**
       * signIn resolves once the session is loaded — unless a second step is owed, in which
       * case it hands back the challenge and nothing is signed in yet.
       */
      const owed = await signIn(email, password);
      if (owed) {
        setChallenge(owed);
        setCode('');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in. Please try again.');
    } finally {
      setBusy(false);
    }
    // The redirect happens through the `session` check above once state has settled.
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    if (!challenge) return;

    setError(null);
    setBusy(true);
    try {
      await completeTwoFactor(challenge.challengeId, code);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code was not accepted.');
      /**
       * The challenge is kept on a wrong code so they can try again, but dropped once it is
       * spent or dead — the API says which, and re-showing a box that cannot work is worse
       * than sending them back to the start.
       */
      if (err instanceof ApiError && /expired|already been used|too many/i.test(err.message)) {
        setChallenge(null);
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * The second step, rendered in the same frame as the sign-in rather than as its own page:
   * it is one act, and bouncing somebody to another screen halfway through a login reads as
   * something having gone wrong.
   */
  if (challenge) {
    return (
      <div className="login-wrap">
        <section className="login-pitch">
          <Brand large />
          <div className="login-pitch-body">
            <div className="eyebrow">Lucentra Systems · Egen Holdings</div>
            <h1>
              One place where the work is <em>managed and logged</em>.
            </h1>
          </div>
          <p className="login-pitch-foot">
            Access is granted by an administrator. There is no public sign-up.
          </p>
        </section>

        <section className="login-form-side">
          <div className="login-card">
            <div className="row-between mb-2">
              <div className="eyebrow" style={{ margin: 0 }}>
                Two steps
              </div>
              <ThemeToggle compact />
            </div>
            <h2 style={{ fontSize: 24, marginBottom: 6 }}>One more step</h2>
            <p className="subtitle mb-2">
              {challenge.method === 'app'
                ? `Open your authenticator and enter the ${challenge.digits}-digit code for Lucentra Hub.`
                : `We have emailed a ${challenge.digits}-digit code to ${challenge.sentTo}. It lasts ${challenge.expiresInMinutes} minutes.`}
            </p>

            {error && (
              <div className="callout" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={submitCode} noValidate>
              <label className="field">
                <span className="field-label">Code</span>
                <input
                  value={code}
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={challenge.digits + 2}
                  placeholder={'0'.repeat(challenge.digits)}
                  onChange={(event) => setCode(event.target.value.replace(/[^\d]/g, ''))}
                  style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 20, letterSpacing: 5 }}
                />
              </label>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                disabled={busy || code.length < challenge.digits}
              >
                {busy ? 'Checking…' : 'Sign in'}
              </button>
            </form>

            <button
              type="button"
              className="btn btn-quiet btn-sm mt-2"
              onClick={() => {
                setChallenge(null);
                setCode('');
                setError(null);
              }}
            >
              Start again
            </button>

            {challenge.method === 'email' && (
              <p className="dim" style={{ fontSize: 12, marginTop: 12 }}>
                No code yet? Start again to have another sent. Each attempt replaces the last,
                so only the newest email will work.
              </p>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <section className="login-pitch">
        <Brand large />
        <div className="login-pitch-body">
          <div className="eyebrow">Lucentra Systems · Egen Holdings</div>
          <h1>
            One place where the work is <em>managed and logged</em>.
          </h1>
          <p className="subtitle" style={{ marginTop: 20, maxWidth: '46ch' }}>
            Delivery, service and time for the Egen group and its customers. Projects and
            priorities, problems driven to a fix, merges approved and recorded, hours booked
            against the work they belong to.
          </p>
        </div>
        <p className="login-pitch-foot">
          Access is granted by an administrator. There is no public sign-up.
        </p>
      </section>

      <section className="login-form-side">
        <div className="login-card">
          <div className="row-between mb-2">
            <div className="eyebrow" style={{ margin: 0 }}>
              Sign in
            </div>
            <ThemeToggle compact />
          </div>
          <h2 style={{ fontSize: 24, marginBottom: 6 }}>Welcome back</h2>
          <p className="subtitle mb-2">Use the work address your account was created with.</p>

          {/*
            WHY THEY ARE LOOKING AT THIS PAGE, when they were working a moment ago.

            Shown only while there is no error of their own to answer: once they have tried to
            sign in and got something wrong, that is the more useful message and two stacked
            callouts is a page arguing with itself.
          */}
          {!error && endedReason && (
            <div className="callout" role="status">
              {endedReason}
            </div>
          )}

          {error && (
            <div className="callout" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <label className="field">
              <span className="field-label">Email address</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="you@lucentra.co.za"
                required
                autoFocus
              />
            </label>

            <label className="field">
              <span className="field-label">Password</span>
              <PasswordField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="login-note">
            <Link to="/forgot-password" style={{ textDecoration: 'underline' }}>
              Forgotten your password?
            </Link>{' '}
            We will email you a link to set a new one.
          </p>
        </div>
      </section>
    </div>
  );
}
